// src/store/services/shopService.js
import { supabase } from '../../supabaseClient';
import Compressor from 'compressorjs';
import { normalizeSearchText } from '../../utils/searchUtilsForAccounts';

const compressImageAsync = (file) => {
    return new Promise((resolve, reject) => {
        new Compressor(file, {
            quality: 0.9,
            maxWidth: 1920,
            maxHeight: 1920,
            success(result) {
                resolve(new File([result], file.name, { type: file.type, lastModified: Date.now() }));
            },
            error(err) {
                reject(err);
            },
        });
    });
};

/**
 * Service functions for interacting with the 'shops' and 'shop_memberships' tables.
 *
 * All functions handle their own Supabase calls and error handling, throwing
 * an error if a problem occurs so the calling component can catch it.
 */

// NEW: statuses that mean "don't show this shop in public browse/search".
// Used only by listing-style queries — direct lookups by id/slug/secret_key
// stay unfiltered on purpose, since order history, admin tools, and shop-login
// flows need to resolve a shop regardless of its current status.
const EXCLUDED_SHOP_STATUSES = ['Deleted', 'Not Active', 'Offline'];

const excludeHiddenShops = (query) => {
    let q = query;
    EXCLUDED_SHOP_STATUSES.forEach((status) => {
        q = q.neq('shop_availability_status', status);
    });
    return q;
};

// READ: Fetch all shops
export const getAllShops = async () => {
    const { data, error } = await excludeHiddenShops(
        supabase.from('shops').select('*')
    );
    if (error) throw error;
    return data;
};

// READ: Fetch a single shop by ID
export const getShopById = async (id) => {
    const { data, error } = await supabase
        .from('shops')
        .select('*')
        .eq('id', id)
        .single();
    if (error) throw error;
    return data;
};

// READ: Fetch a single shop by its secret key
// This function is now simple and only returns the shop object.
export const getShopBySecretKey = async (secretKey) => {
    const { data, error } = await supabase
        .from('shops')
        .select('*')
        .eq('secret_key', secretKey)
        .single();
    
    // If no data is found, a `PGRST116` error is thrown. We check for that
    // specifically and return null instead of throwing an error for a non-existent key.
    if (error && error.code !== 'PGRST116') {
        throw error;
    }
    return data; // `data` will be the shop object or `null` if not found
};

// CREATE: Create a new shop and link the owner to it
// This function now takes the shop payload AND the user's ID.
// export const createShop = async (payload, ownerId) => {
//     if (!ownerId) {
//         throw new Error('Owner ID is required to create a shop.');
//     }

//     // 1. Insert the new shop record
//     const { data: newShop, error: shopError } = await supabase
//         .from('shops')
//         .insert(payload)
//         .select()
//         .single();

//     if (shopError) throw shopError;

//     // 2. Link the creating user to the new shop as an 'owner'
//     // This insert now uses an array to ensure the database auto-generates the UUID for the 'id' column.
//     const { data: membership, error: membershipError } = await supabase
//         .from('shop_memberships')
//         .insert([
//             {
//                 user_id: ownerId,
//                 shop_id: newShop.id,
//                 role: 'owner'
//             }
//         ])
//         .select()
//         .single();

//     if (membershipError) throw membershipError;

//     // Return the newly created shop record and the membership details
//     return { ...newShop, membership };
// };

// UPDATE: Update an existing shop
export const updateShop = async (id, payload) => {
    const finalPayload = { ...payload };

    // Keep the accent-insensitive search column in sync whenever the name changes
    if (typeof finalPayload.name === 'string') {
        finalPayload.search_name_normalized = normalizeSearchText(finalPayload.name);
    }

    const { data, error } = await supabase
        .from('shops')
        .update(finalPayload)
        .eq('id', id)
        .select()
        .single();
    if (error) throw error;
    return data;
};

// export const uploadShopLogo = async (file, userId, shopId = null) => {

//     if (!file) return 'N/A';

//     try {
//         const compressedFile = await compressImageAsync(file);

//         // Make sure your netlify function can handle type='shop_logo' or default gracefully
//         const authResponse = await fetch(`/.netlify/functions/auth-imagekit?type=shop_logo`);
//         const authData = await authResponse.json();

//         if (!authResponse.ok) throw new Error(authData.message || 'Failed to fetch ImageKit credentials.');

//         const formData = new FormData();
//         const publicKey = import.meta.env.VITE_IMAGEKIT_PUBLIC_KEY;
        
//         // FLAT FOLDER STRUCTURE FOR LOGOS
//         const folderPath = `/porte17/shops/logos`;

//         formData.append('file', compressedFile);
//         formData.append('fileName', compressedFile.name);
//         formData.append('publicKey', publicKey);
//         formData.append('expire', authData.expire);
//         formData.append('token', authData.token);
//         formData.append('signature', authData.signature);
//         formData.append('folder', folderPath);

//         const uploadResponse = await fetch('https://upload.imagekit.io/api/v1/files/upload', {
//             method: 'POST',
//             body: formData,
//         });

//         const uploadData = await uploadResponse.json();
//         if (!uploadResponse.ok) throw new Error(uploadData.message || 'ImageKit upload network failure.');

//         // TRACK IMAGE IN SUPABASE
//         await supabase.from('tracked_images').insert([{
//             file_id: uploadData.fileId,
//             url: uploadData.url,
//             file_path: uploadData.filePath,
//             entity_type: 'shop',
//             image_type: 'shop_logo',
//             uploaded_by: userId,
//             shop_id: shopId,
//             width: uploadData.width || null,
//             height: uploadData.height || null,
//             file_size_bytes: compressedFile.size
//         }]);

//         return uploadData.url;
//     } catch (error) {
//         console.error('Image handling failed:', error);
//         throw error;
//     }
// };

// DELETE: Delete a shop by ID


export const deleteShop = async (id) => {
    const { error } = await supabase
        .from('shops')
        .delete()
        .eq('id', id);
    if (error) throw error;
    return true;
};



//////////////////// For Shop Logins ////////////////////////////////
// src/store/services/shopService.js

// --- Core Membership Management ---

// NEW: Search for shops by name or slug
export const searchShopsForLogin = async (searchTerm) => {
    if (!searchTerm) return [];
    const { data, error } = await supabase
        .from('shops')
        .select('id, name, slug')
        .or(`name.ilike.%${searchTerm}%,slug.ilike.%${searchTerm}%`)
        .limit(10); // Limit results for efficiency

    if (error) throw error;
    return data;
};

// NEW: Search for profiles by username
export const searchProfilesForLogin = async (searchTerm) => {
    if (!searchTerm) return [];
    const { data, error } = await supabase
        .from('profiles')
        .select('id, username')
        .ilike('username', `%${searchTerm}%`)
        .limit(10); // Limit results for efficiency

    if (error) throw error;
    return data;
};











// ../store/services/shopService.js

/**
 * Recalculates and updates a user's global profile role based on their memberships.
 * Protects higher tier roles like admins from being overwritten.
 */
// ../store/services/shopService.js

/**
 * Recalculates and updates a user's global profile role based on their memberships.
 * Safe against breaking admin privileges.
 */
export const recalculateUserProfileRole = async (userId) => {
    // 1. Fetch current profile role to protect admins
    const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', userId)
        .single();

    if (profileError) {
        console.error("Error fetching profile for recalculation:", profileError);
        throw profileError;
    }

    // Protect administrative accounts
    if (['admin_1', 'admin_2', 'admin_3'].includes(profile.role)) {
        return profile.role;
    }

    // 2. Count active shop ownerships across the app
    const { data: memberships, error: memberError } = await supabase
        .from('shop_memberships')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'owner');

    if (memberError) {
        console.error("Error checking memberships for recalculation:", memberError);
        throw memberError;
    }

    let targetGlobalRole = 'user_1';
    if (memberships && memberships.length > 0) {
        targetGlobalRole = 'shop_owner';
    }

    // 3. Perform the update if the role state has shifted
    if (profile.role !== targetGlobalRole) {
        const { error: updateError } = await supabase
            .from('profiles')
            .update({ role: targetGlobalRole })
            .eq('id', userId);

        if (updateError) {
            console.error("Error updating profile role. If this fails, check your Supabase RLS policies for the profiles table:", updateError);
            throw updateError;
        }
    }

    return targetGlobalRole;
};

// ../store/services/shopService.js

export const addShopMember = async (shopId, userId, role) => {
    const { data: existingMember, error: checkError } = await supabase
        .from('shop_memberships')
        .select('id')
        .eq('user_id', userId)
        .eq('shop_id', shopId)
        .single();

    if (checkError && checkError.code !== 'PGRST116') throw checkError;
    if (existingMember) throw new Error('This user is already a member of this shop.');

    const { data, error } = await supabase
        .from('shop_memberships')
        .insert([{ user_id: userId, shop_id: shopId, role: role }])
        .select()
        .single();

    if (error) throw error;
    return data; // Database trigger handles the complex role calculation automatically
};

export const removeShopMember = async (membershipId) => {
    const { data: membership, error: fetchError } = await supabase
        .from('shop_memberships')
        .select('user_id')
        .eq('id', membershipId)
        .single();

    if (fetchError) throw fetchError;

    const { error: deleteError } = await supabase
        .from('shop_memberships')
        .delete()
        .eq('id', membershipId);

    if (deleteError) throw deleteError;

    return { success: true, userId: membership.user_id };
};


// NEW: Function to fetch all shops a user is a member of
export const fetchUserShopMemberships = async (userId) => {
    const { data, error } = await supabase
        .from('shop_memberships')
        .select(`
            shop_id,
            role,
            shops (id, name, slug, shop_logo_url, shop_type, verification_status)
        `)
        .eq('user_id', userId);

    if (error) throw error;
    // The query returns objects with a 'shops' object inside.
    // We transform this to a flatter array for easier consumption.
    return data.map(membership => ({
        membershipId: membership.id,
        role: membership.role,
        shop: membership.shops
    }));
};

// NEW: Function to get a shop's details by ID (used after login selection)
export const fetchShopById = async (shopId) => {
    const { data, error } = await supabase
        .from('shops')
        .select('*')
        .eq('id', shopId)
        .single();

    if (error) throw error;
    return data;
};

// NEW: Function to fetch existing shop members for the owner's page
export const fetchShopMembers = async (shopId) => {
    const { data, error } = await supabase
        .from('shop_memberships')
        .select(`
            id,
            role,
            created_at,
            profiles (id, username, email)
        `)
        .eq('shop_id', shopId);

    if (error) throw error;
    return data;
};


/**
 * NEW: Fetch a single shop by ID to display its details in the variant view.
 * @param {string} shopId The ID of the shop to fetch.
 * @returns {Promise<object>} The shop object.
 */
export const getShopDetailsByVariantShopId = async (shopId) => {
    if (!shopId) {
        throw new Error('Shop ID is required to fetch shop details.');
    }
    
    // 💥 FIX HERE: Explicitly select all columns EXCEPT 'secret_key'
    const { data, error } = await supabase
        .from('shops')
        .select(`
            id, 
            name, 
            slug, 
            shop_logo_url, 
            description, 
            images, 
            contact_info, menu_categories, service_policy, verification_status, shop_availability_status,
            shop_type, 
            location, 
            hours, 
            rating,
            created_by,
            created_at,
            updated_at
        `) // <-- Removed 'secret_key' from the selection list
        .eq('id', shopId)
        .single();
    
    if (error) {
        console.error('Error fetching shop details:', error);
        throw error;
    }
    
    return data;
};


// src/store/services/shopService.js (Add these new functions)

// ... existing imports and functions ...

/**
 * UPDATE: Update a shop's search_tags column.
 * @param {string} shopId - The ID of the shop to update.
 * @param {Array<string>} tags - The new array of search tags.
 * @returns {Promise<object>} The updated shop record.
 */
export const updateShopSearchTags = async (shopId, tags) => {
    // The payload is structured to update only the search_tags field
    const payload = { search_tags: tags };
    
    const { data, error } = await supabase
        .from('shops')
        .update(payload)
        .eq('id', shopId)
        .select()
        .single();
    
    if (error) throw error;
    return data;
};

/**
 * UPDATE: Update a shop's featured_offerings column.
 * @param {string} shopId - The ID of the shop to update.
 * @param {object} offerings - The new JSON object for featured offerings (e.g., {en: [], fr: []}).
 * @returns {Promise<object>} The updated shop record.
 */
export const updateShopFeaturedOfferings = async (shopId, offerings) => {
    // The payload is structured to update only the featured_offerings field
    const payload = { featured_offerings: offerings };
    
    const { data, error } = await supabase
        .from('shops')
        .update(payload)
        .eq('id', shopId)
        .select()
        .single();
    
    if (error) throw error;
    return data;
};

////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////

// src/services/shopService.js (CORRECTED for Tag and Type Search)

const SHOPS_PER_PAGE = 20;
// src/services/shopService.js (Confirmed structure)

// ... (Your existing searchShops function remains the same) ...

/**
 * Fetches all shops whose location matches one of the provided quarter names.
 * @param {Array<string>} quarterNames Array of quarter names (L0, L1, L2, etc.).
 * @returns {Array<Object>} List of shop objects.
 */
export async function getShopsByQuarterProximity(quarterNames) {
    if (!quarterNames || quarterNames.length === 0) {
        return [];
    }
    
    // Increased limit for proximity results
    const LIMIT = 100; 

    console.log(`[ShopService] Starting proximity search for ${quarterNames.length} quarters.`);

    let query = supabase
        .from('shops')
        .select(`id,
                 name,
                 slug,
                 shop_logo_url,
                 hours,
                 contact_info,
                 menu_categories,
                 verification_status,shop_availability_status,
                 service_policy,
                 description,
                 shop_type,
                 location,
                 rating,
                 featured_offerings,
                 search_tags`)
        .not('location->>quarter', 'is', null)
        .in('location->>quarter', quarterNames) 
        .limit(LIMIT);

    // NEW: hide deleted / not active / offline shops from proximity results
    query = excludeHiddenShops(query);

    query = query.order('name', { ascending: true });

    const { data, error } = await query;

    if (error) {
        console.error('[ShopService] Proximity Query Error:', error);
        throw new Error(`Failed to fetch shops by proximity: ${error.message}`);
    }

    console.log(`[ShopService] Proximity Search Success: ${data.length} shops found.`);

    return data || [];
}

// src/services/shopService.js

// ... (existing imports and SHOPS_PER_PAGE)

/**
 * Executes a paginated, combined search for shops based on a text query,
 * optionally filtered by a specific quarter.
 *
 * @param {string} searchTerm The user's search query (already lowercased).
 * @param {number} page The page number to fetch (0-indexed).
 * @param {string | null} [quarterName=null] Optional quarter name to filter results by.
 */
// src/services/shopService.js

// ... (Your getShopsByQuarterProximity function is now deprecated/removed) ...

/**
 * Executes a paginated, combined search for shops based on a text query,
 * optionally filtered by a specific quarter.
 *
 * @param {string} searchTerm The user's search query (already lowercased).
 * @param {number} page The page number to fetch (0-indexed).
 * @param {string | null} [quarterName=null] Optional quarter name to filter results by.
 */
export async function searchShops(searchTerm, page, quarterName = null) {
    
    // 1. --- Normalize and Prepare Query ---
    const normalizeQuery = (text) => {
        // Must match the PostgreSQL UNACCENT/LOWER logic
        return text.toLowerCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "") 
            .trim();
    };

    const normalizedTerm = normalizeQuery(searchTerm); // 🛑 NEW: Normalize the input term
    
    // CRITICAL: Build the multi-word, partial-match pattern for ILIKE
    // Example: "elec repair" -> "%elec% AND %repair%"
    const ilikePattern = normalizedTerm.split(/\s+/).filter(Boolean)
        .map(word => `%${word}%`)
        .join(' AND '); 

    const start = page * SHOPS_PER_PAGE;
    const end = start + SHOPS_PER_PAGE - 1;

    console.log(`[ShopService] Starting flexible search for pattern: "${ilikePattern}" (Page: ${page}, Range: ${start}-${end})`);
    
    let query = supabase
        .from('shops')
        .select(`id,
                 name,
                 slug,
                 shop_logo_url,
                 hours,
                 contact_info,
                 menu_categories,
                 verification_status,
                 service_policy, shop_availability_status,
                 description,
                 shop_type,
                 location,
                 rating,
                 featured_offerings,
                 search_tags`);

    if (normalizedTerm.length < 2) {
        // We only proceed to the database with a meaningful query length
        console.log('[ShopService] Search term is too short, returning empty results.');
        return { data: [], nextCursor: null }; 
    }

    // 2. --- CRITICAL FIX: Replace Rigid Query with Flexible ILIKE on Vector ---
    // This single line replaces all previous OR conditions for tags, name, and shop_type.
    // Why? The 'search_tags_vector' column already contains the shop name, normalized tags, and shop type,
    // making this single ILIKE query sufficient and highly performant due to the GIN index.
    query = query.ilike('search_tags_vector', ilikePattern); 
    
    
    // 3. --- Apply Optional Quarter Filter (AND condition) ---
    if (quarterName) {
        console.log(`[ShopService] Applying quarter filter: ${quarterName}`);
        // Filter by the exact 'quarter' key inside the 'location' JSONB column
        query = query.eq('location->>quarter', quarterName); 
    }

    // NEW: hide deleted / not active / offline shops from search results
    query = excludeHiddenShops(query);

    // 4. --- Ordering and Pagination ---
    // Note: The flexible search inherently handles name/tag relevance well, but we keep ordering for stability.
    query = query.order('name', { ascending: true });
    query = query.order('created_at', { ascending: false }); 

    query = query.range(start, end);

    const { data, error } = await query;

    // ... (rest of error handling and return logic is the same)
    if (error) {
        console.error('[ShopService] Supabase Query Error:', error);
        throw new Error(`Failed to fetch shops: ${error.message}`);
    }

    const hasNext = data.length === SHOPS_PER_PAGE;
    const nextCursor = hasNext ? page + 1 : null;
    
    console.log(`[ShopService] Search Success: ${data.length} shops found for "${normalizedTerm}"${quarterName ? ` in ${quarterName}` : ''}.`);

    return {
        data: data || [],
        nextCursor: nextCursor,
    };
}

// NOTE: You can now remove the entire `getShopsByQuarterProximity` function as 
// it's no longer needed. The combined search approach replaces it.
// The `useProximityShops` hook will be removed/replaced next.


// src/store/services/shopService.js

// ... (Existing imports and functions) ...

/**
 * READ: Fetch a single shop's public page data by its slug.
 * @param {string} slug The unique URL slug of the shop.
 * @returns {Promise<object>} The shop object including page configuration.
 */
export const getShopPageDataBySlug = async (slug) => {
    if (!slug) {
        throw new Error('Slug is required to fetch shop page data.');
    }

    // Added missing fields: created_at, amenities, anchor_success_stories, and badge flags
    const { data, error } = await supabase
        .from('shops')
        .select(`
            id, 
            name, 
            shop_logo_url, 
            description, 
            images, 
            contact_info,
            menu_categories,
            service_policy, 
            verification_status,
            shop_type, 
            location, 
            hours, 
            rating,
            slug,
            page_layout_config,
            featured_offerings,
            policies,
            brand_colors,
            created_at,
            amenities,
            anchor_success_stories,
            badge_trusted_hand,
            badge_a_game,
            badge_top_expert,
            shop_availability_status
        `) 
        .eq('slug', slug)
        .single();
    
    if (error && error.code !== 'PGRST116') {
        console.error('Error fetching shop by slug:', error);
        throw error;
    }

    // NEW: this is a direct link (e.g. from a bookmark, a shared link, an order
    // history entry), so we still return the data — just flagged — rather than
    // hard-filtering it out the way the browse/search functions do.
    if (data && EXCLUDED_SHOP_STATUSES.includes(data.shop_availability_status)) {
        return { ...data, isInactive: true };
    }
    
    return data;
};
/////////////////////////// Shop Editors //////////////////////////////////////////
// src/services/shopService.js
/**
 * Updates the location and contact information for a specific shop.
 * @param {string} shopId The ID of the shop to update.
 * @param {object} updatedLocation The new location object.
 * @param {object} updatedContact The new contact info object.
 * @returns {object} The updated shop data or an error.
 */
export const updateShopLocationAndContact = async (shopId, updatedLocation, updatedContact) => {
    try {
        const { data, error } = await supabase
            .from('shops')
            .update({
                location: updatedLocation,
                contact_info: updatedContact,
                updated_at: new Date().toISOString(),
            })
            .eq('id', shopId)
            .select()
            .single();

        if (error) {
            throw new Error(`Supabase update error: ${error.message}`);
        }

        return data;
    } catch (error) {
        console.error("Error updating shop location and contact:", error);
        throw error;
    }
};

export const updateShopHours = async (shopId, updatedHours) => {
    try {
        const { data, error } = await supabase
            .from('shops')
            .update({
                hours: updatedHours,
                updated_at: new Date().toISOString(),
            })
            .eq('id', shopId)
            .select()
            .single();

        if (error) {
            throw new Error(`Supabase update error: ${error.message}`);
        }

        return data;
    } catch (error) {
        console.error("Error updating shop hours:", error);
        throw error;
    }
};


// src/services/shopService.js

/**
 * Updates the text policies and payment options for a specific shop.
 * * @param {string} shopId The ID of the shop to update.
 * @param {object} policyFieldsAndPaymentOptions The data object containing 
 * return_policy, cancellation_policy, delivery_policy, and payment_options.
 * @returns {object} The updated shop data or an error.
 */
export const updateShopPolicies = async (shopId, policyFieldsAndPaymentOptions) => {
    
    // 🚨 CRITICAL FIX: The policy data must be nested under the 'policies' key
    // because that is the name of the JSONB column in your database.
    const updatePayload = {
        policies: policyFieldsAndPaymentOptions, 
        updated_at: new Date().toISOString(),
    };

    try {
        const { data, error } = await supabase
            .from('shops')
            // Send the correctly structured payload to update the 'policies' column
            .update(updatePayload) 
            .eq('id', shopId)
            .select()
            .single();

        if (error) {
            throw new Error(`Supabase update error: ${error.message}`);
        }

        return data;
    } catch (error) {
        console.error("Error updating shop policies:", error);
        throw error;
    }
};




export const updateShopDeliveryZones = async (shopId, updatedDelivery) => {
    try {
        // 1. Fetch the current shop data to get the existing page_layout_config
        const { data: currentShopData, error: fetchError } = await supabase
            .from('shops')
            .select('page_layout_config')
            .eq('id', shopId)
            .single();

        if (fetchError) {
            throw new Error(`Supabase fetch error during delivery update: ${fetchError.message}`);
        }

        const currentLayout = currentShopData.page_layout_config;
        let layoutToUpdate = Array.isArray(currentLayout) ? [...currentLayout] : [];
        
        // Find the TimingsDeliverySection component
        const componentIndex = layoutToUpdate.findIndex(
            comp => comp.component === 'TimingsDeliverySection'
        );

        // 2. Modify the properties of the TimingsDeliverySection component
        if (componentIndex !== -1) {
            const updatedProps = {
                ...layoutToUpdate[componentIndex].props,
                // Update the deliveryQuarters array in the component's props
                deliveryQuarters: updatedDelivery.delivery_quarters,
                // We'll trust the component's presence implies enablement, or handle the flag client-side
            };

            layoutToUpdate[componentIndex].props = updatedProps;
        } else if (updatedDelivery.isDeliveryEnabled && updatedDelivery.delivery_quarters.length > 0) {
            // If the component is missing but the user enabled delivery, create the component entry
            layoutToUpdate.push({
                order: layoutToUpdate.length + 1,
                props: {
                    deliveryQuarters: updatedDelivery.delivery_quarters,
                    deliveryPolicy: "Policy text is needed here." // Placeholder for the delivery policy text
                },
                component: "TimingsDeliverySection"
            });
        }
        
        // 3. Perform the update operation - ONLY UPDATE EXISTING COLUMNS
        const updatePayload = {
            page_layout_config: layoutToUpdate,
            updated_at: new Date().toISOString(),
            // REMOVED: isDeliveryEnabled and delivery_quarters since they don't exist as top-level columns.
        };

        const { data, error: updateError } = await supabase
            .from('shops')
            .update(updatePayload)
            .eq('id', shopId)
            .select() // Return the complete updated row
            .single();

        if (updateError) {
            throw new Error(`Supabase update error: ${updateError.message}`);
        }

        return data;
    } catch (error) {
        console.error("Error updating shop delivery zones:", error);
        throw error;
    }
};


// ------------------------For Admin Edit Shops ---------------------------------
// READ: Fetch a limited range of shops for admin management (Paginated)
// NOTE: deliberately NOT filtered by EXCLUDED_SHOP_STATUSES — admins need to
// see Deleted/Not Active/Offline shops in this list to manage them.
export const fetchPaginatedShopsForAdminList = async (page = 0, limit = 15) => {
    const start = page * limit;
    const end = start + limit - 1;

    const { data, error, count } = await supabase
        .from('shops')
        .select('*', { count: 'exact' })
        .order('name', { ascending: true })
        .range(start, end);

    if (error) throw error;
    return { data, totalCount: count };
};

// READ: Fetch specialized inventory for Food and Beverage establishments
export const fetchFoodAndBeverageInventoryByShopId = async (shopId) => {
    const { data, error } = await supabase
        .from('food_menu_items')
        .select('*')
        .eq('shop_id', shopId)
        .order('category', { ascending: true });

    if (error) throw error;
    return data;
};

// READ: Fetch professional services offered by a shop
export const fetchProfessionalServicesByShopId = async (shopId) => {
    const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
};

// READ: Fetch product variants for retail-focused shops
export const fetchProductVariantsByShopId = async (shopId) => {
    const { data, error } = await supabase
        .from('variants')
        .select('*')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
};






// ... (keep your existing functions like getAllShops, etc.)

/**
 * Administrative Update for Inventory Items
 * Handles name mapping: 'product_name' for variants, 'name' for others.
 */
export const adminUpdateItemDetails = async (id, mode, updates) => {
    // 1. Normalize the table name for the .from() method
    const table = mode.toLowerCase();
    
    // 2. Prepare the payload
    let finalPayload = { ...updates };

    /**
     * STERN COLUMN MAPPING:
     * We check for 'variants' or 'retail_variants' (case-insensitive)
     */
    const isVariantTable = table.includes('variant');

    if (isVariantTable) {
        // If the UI sent 'name', rename it to 'product_name'
        if (finalPayload.hasOwnProperty('name')) {
            finalPayload.product_name = finalPayload.name;
            delete finalPayload.name; 
        }
        
        // Safety: Ensure 'variant_name' isn't being sent as 'name' either
        if (finalPayload.hasOwnProperty('variant_name') && !finalPayload.product_name) {
             finalPayload.product_name = finalPayload.variant_name;
             delete finalPayload.variant_name;
        }
    }

    // 3. Execute the update
    const { data, error } = await supabase
        .from(table)
        .update(finalPayload)
        .eq('id', id)
        .select()
        .single();

    if (error) {
        console.error(`PostgREST Error (${error.code}):`, error.message);
        throw error;
    }
    
    return data;
};


const getTableName = (mode) => {
    const m = mode.toUpperCase();
    // Match your SQL exactly
    if (m === 'FOOD_BEVERAGE' || m === 'FOOD_AND_BEVERAGE') return 'food_menu_items';
    if (m === 'PROFESSIONAL_SERVICES' || m === 'SERVICES') return 'services';
    if (m === 'RETAIL_VARIANTS') return 'variants';
    return mode.toLowerCase();
};

export const adminDeleteItem = async (id, mode) => {
    const table = getTableName(mode);
    const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', String(id));

    if (error) throw error;
    return true;
};

export const adminCreateNewInventoryItem = async (mode, payload) => {
    const table = getTableName(mode);
    
    let cleanPayload = { ...payload };

    // Price must be stringified for JSONB columns
    if (cleanPayload.price && typeof cleanPayload.price === 'object') {
        cleanPayload.price = JSON.stringify(cleanPayload.price);
    }

    const { data, error } = await supabase
        .from(table)
        .insert([cleanPayload])
        .select()
        .single();

    if (error) throw error;
    return data;
};

/**
 * Update the category list for a shop
 */
export const adminUpdateShopCategories = async (shopId, categories) => {
    // FIXED: the previous version referenced `service_policy` and
    // `verification_status` as bare identifiers with no such variables in
    // scope — calling it as originally written would throw a
    // ReferenceError immediately. This function only ever needed to touch
    // menu_categories.
    const { data, error } = await supabase
        .from('shops')
        .update({ menu_categories: categories })
        .eq('id', shopId)
        .select()
        .single();

    if (error) throw error;
    return data;
};

// -------------------- Get Menu Items From Tables ------------------------------------

// services/shopService.js
export const fetchServicesByShopId = async (shopId) => {
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .eq('shop_id', shopId)
    .eq('is_available', true)
    .eq('is_hidden', false)
    .order('category', { ascending: true });

  if (error) throw new Error(error.message);
  return data;
};





// src/store/services/shopService.js

/**
 * Atomic update for a shop's high-level classification and policies
 * Updates: business_style inside shop_type, home service inside service_policy, and shop_type itself
 */
export const adminUpdateShopCoreConfig = async (shopId, { currentShopType, currentServicePolicy, updates }) => {
  // 1. Prepare modern deep merged objects for JSONB properties
  let updatedShopType = { ...currentShopType };
  if (updates.shopTypeObject) {
    // If shop type object changed, adopt its properties completely
    updatedShopType = {
      ...updatedShopType,
      ...updates.shopTypeObject,
    };
  }
  if (updates.businessStyle) {
    updatedShopType.business_style = updates.businessStyle;
  }

  let updatedServicePolicy = { ...currentServicePolicy };
  if (updates.acceptsHomeService !== undefined) {
    updatedServicePolicy.accepts_home_service = updates.acceptsHomeService;
  }

  // 2. Execute the mutation to the core row
  const { data, error } = await supabase
    .from('shops')
    .update({
      shop_type: updatedShopType,
      service_policy: updatedServicePolicy
    })
    .eq('id', shopId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Updates the operational profile metrics of the business
 */
export const adminUpdateShopOperationalProfile = async (shopId, { currentOperationalProfile, updates }) => {
  const updatedProfile = {
    ...currentOperationalProfile,
    ...updates
  };

  const { data, error } = await supabase
    .from('shops')
    .update({
      operational_profile: updatedProfile
    })
    .eq('id', shopId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Toggles storewide on_sale status and updates all corresponding inventory items.
 *
 * @param {Object} params
 * @param {string|number} params.shopId - The shop ID
 * @param {string} params.businessStyle - Value from currentShop.shop_type.business_style
 * @param {boolean} params.isOnSale - Target on_sale state (true or false)
 */
export const updateShopOnSaleStatus = async ({ shopId, businessStyle, isOnSale }) => {
    // 1. Update the shop record
    const { error: shopError } = await supabase
        .from('shops')
        .update({ on_sale: isOnSale })
        .eq('id', shopId);

    if (shopError) throw shopError;

    // 2. Determine target table based on shop business style
    const style = String(businessStyle || '').trim().toLowerCase();
    let tableName = null;

    if (style === 'services') {
        tableName = 'services';
    } else if (style === 'products') {
        tableName = 'variants';
    } else if (style === 'food & beverage' || style.includes('food')) {
        tableName = 'food_menu_items';
    }

    // 3. Bulk update all items for this shop if a matching table was found
    if (tableName) {
        const { error: itemsError } = await supabase
            .from(tableName)
            .update({ on_sale: isOnSale })
            .eq('shop_id', shopId);

        if (itemsError) throw itemsError;
    }

    return { shopId, isOnSale, tableName };
};






// ---- APPEND THESE TO src/store/services/shopService.js ----

/**
 * Resolves the correct inventory table name for a shop based on its business_style.
 * @param {string} businessStyle - currentShop.shop_type.business_style
 * @returns {string|null} table name, or null if unrecognized
 */
const getOnSaleTableName = (businessStyle) => {
    const style = String(businessStyle || '').trim().toLowerCase();
    if (style === 'services') return 'services';
    if (style === 'products') return 'variants';
    if (style === 'food & beverage' || style.includes('food')) return 'food_menu_items';
    return null;
};

/**
 * READ: Fetch all items (variants / services / food_menu_items) for a shop,
 * normalized to a common shape so the on-sale toggle modal can render them
 * regardless of business style.
 *
 * @param {string} shopId
 * @param {string} businessStyle - currentShop.shop_type.business_style
 * @returns {Promise<{table: string|null, items: Array}>}
 */
export const fetchShopItemsForOnSaleToggle = async (shopId, businessStyle) => {
    const table = getOnSaleTableName(businessStyle);
    if (!table || !shopId) return { table, items: [] };

    // variants stores the name under 'product_name'; services/food_menu_items use 'name'
    const nameColumn = table === 'variants' ? 'product_name' : 'name';

    const { data, error } = await supabase
        .from(table)
        .select(`id, ${nameColumn}, image_url, price, on_sale`)
        .eq('shop_id', shopId)
        .order(nameColumn, { ascending: true });

    if (error) throw error;

    const items = (data || []).map((item) => {
        let parsedPrice = item.price;
        if (typeof parsedPrice === 'string') {
            try { parsedPrice = JSON.parse(parsedPrice); } catch (e) { parsedPrice = null; }
        }
        return {
            id: item.id,
            name: item[nameColumn] || '',
            image_url: item.image_url || '',
            price: parsedPrice || {},
            on_sale: Boolean(item.on_sale),
        };
    });

    return { table, items };
};

export const bulkUpdateItemsOnSale = async ({ table, updates, shopId }) => {
    if (!table || !updates || updates.length === 0) return { shopOnSale: null };

    const results = await Promise.all(
        updates.map((u) => supabase.from(table).update({ on_sale: u.on_sale }).eq('id', u.id))
    );
    const errored = results.find((r) => r.error);
    if (errored) throw errored.error;

    if (!shopId) return { shopOnSale: null };

    // Shop is "on sale" when at least one item is on sale.
    const { count, error: countError } = await supabase
        .from(table)
        .select('id', { count: 'exact', head: true })
        .eq('shop_id', shopId)
        .eq('on_sale', true);
    if (countError) throw countError;

    const shopOnSale = (count || 0) > 0;
    const { error: shopError } = await supabase
        .from('shops')
        .update({ on_sale: shopOnSale, updated_at: new Date().toISOString() })
        .eq('id', shopId);
    if (shopError) throw shopError;

    return { shopOnSale };
};
// --------------------------------------------------------------

/**
 * Upload single logo image to ImageKit and track in Supabase
 */
export const uploadShopLogo = async (file, userId, shopId = null) => {
  if (!file) return '';

  try {
    const compressedFile = await compressImageAsync(file);

    const authResponse = await fetch(`/.netlify/functions/auth-imagekit?type=shop_logo`);
    const authData = await authResponse.json();
    if (!authResponse.ok) throw new Error(authData.message || 'Failed to fetch ImageKit credentials.');

    const publicKey = import.meta.env.VITE_IMAGEKIT_PUBLIC_KEY;
    const folderPath = `/porte17/shops/logos`;

    const formData = new FormData();
    formData.append('file', compressedFile);
    formData.append('fileName', `${Date.now()}-${compressedFile.name}`);
    formData.append('publicKey', publicKey);
    formData.append('expire', authData.expire);
    formData.append('token', authData.token);
    formData.append('signature', authData.signature);
    formData.append('folder', folderPath);

    const uploadResponse = await fetch('https://upload.imagekit.io/api/v1/files/upload', {
      method: 'POST',
      body: formData,
    });

    const uploadData = await uploadResponse.json();
    if (!uploadResponse.ok) throw new Error(uploadData.message || 'Logo upload network failure.');

    // Track Image in Supabase
    await supabase.from('tracked_images').insert([{
      file_id: uploadData.fileId,
      url: uploadData.url,
      file_path: uploadData.filePath,
      entity_type: 'shop',
      image_type: 'shop_logo',
      uploaded_by: userId,
      shop_id: shopId,
      width: uploadData.width || null,
      height: uploadData.height || null,
      file_size_bytes: compressedFile.size
    }]);

    return uploadData.url;
  } catch (error) {
    console.error('Logo upload error:', error);
    throw error;
  }
};

/**
 * Upload multiple shop photos to ImageKit (/porte17/shops/shop-photos) and track in Supabase
 */
export const uploadShopPhotos = async (filesArray, userId, shopId = null) => {
  if (!filesArray || filesArray.length === 0) return [];

  const uploadedUrls = [];
  const publicKey = import.meta.env.VITE_IMAGEKIT_PUBLIC_KEY;
  const folderPath = `/porte17/shops/shop-photos`;

  for (let i = 0; i < Math.min(filesArray.length, 5); i++) {
    const file = filesArray[i];
    if (!file) continue;

    try {
      const authResponse = await fetch(`/.netlify/functions/auth-imagekit?type=shop_photo`);
      const authData = await authResponse.json();
      if (!authResponse.ok) throw new Error("Authentication signature missing for photos.");

      const compressedFile = await compressImageAsync(file);

      const formData = new FormData();
      formData.append('file', compressedFile);
      formData.append('fileName', `photo-${Date.now()}-${compressedFile.name}`);
      formData.append('publicKey', publicKey);
      formData.append('expire', authData.expire);
      formData.append('token', authData.token);
      formData.append('signature', authData.signature);
      formData.append('folder', folderPath);

      const response = await fetch('https://upload.imagekit.io/api/v1/files/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error("Shop photo upload failed.");

      const result = await response.json();
      uploadedUrls.push(result.url);

      // Track Image in Supabase
      await supabase.from('tracked_images').insert([{
        file_id: result.fileId,
        url: result.url,
        file_path: result.filePath,
        entity_type: 'shop',
        image_type: 'shop_photo',
        uploaded_by: userId,
        shop_id: shopId,
        width: result.width || null,
        height: result.height || null,
        file_size_bytes: compressedFile.size
      }]);

    } catch (err) {
      console.error(`Failed photo upload at index [${i}]:`, err);
    }
  }

  return uploadedUrls;
};

/**
 * Check if shop name already exists (case insensitive)
 */
export const checkShopNameExists = async (shopName) => {
  if (!shopName || !shopName.trim()) return false;
  const { data, error } = await supabase
    .from('shops')
    .select('id')
    .ilike('name', shopName.trim())
    .maybeSingle();

  if (error) {
    console.error("Error checking shop name availability:", error);
    return false;
  }
  return !!data;
};

/**
 * Create a new shop with automatic image handling & database linking
 */
export const createShop = async ({ payload, ownerId, logoFile = null, photoFiles = [] }) => {
  if (!ownerId) {
    throw new Error('Owner ID is required to create a shop.');
  }

  const { data: newShop, error: shopError } = await supabase
    .from('shops')
    .insert([payload])
    .select()
    .single();

  if (shopError) throw shopError;

  let finalLogoUrl = payload.shop_logo_url || '';
  let finalPhotoUrls = [];

  if (logoFile) {
    finalLogoUrl = await uploadShopLogo(logoFile, ownerId, newShop.id);
  }

  if (photoFiles && photoFiles.length > 0) {
    finalPhotoUrls = await uploadShopPhotos(photoFiles, ownerId, newShop.id);
  }

  // FIX: images is a jsonb column — pass the plain array so Supabase
  // serializes it correctly, instead of double-encoding it as a JSON
  // string stored inside the JSON column.
  const { data: updatedShop, error: updateError } = await supabase
    .from('shops')
    .update({
      shop_logo_url: finalLogoUrl,
      images: finalPhotoUrls
    })
    .eq('id', newShop.id)
    .select()
    .single();

  if (updateError) throw updateError;

  const { data: membership, error: membershipError } = await supabase
    .from('shop_memberships')
    .insert([
      {
        user_id: ownerId,
        shop_id: newShop.id,
        role: 'owner'
      }
    ])
    .select()
    .single();

  if (membershipError) throw membershipError;

  return { ...updatedShop, membership };
};
/**
 * Admin approves application to go live
 */
export const approveShop = async (shopId, adminId) => {
  const { data, error } = await supabase
    .from('shops')
    .update({
      created_by: adminId,
      approved_by: adminId,
      // FIX: verification_status is jsonb — pass the plain object so
      // Supabase serializes it correctly, same bug class as the
      // createShop/images fix from earlier.
      verification_status: {
        method: "business_registration_and_website_check",
        status: "verified",
        pro_business: "true"
      },
      shop_availability_status: "Offline"
    })
    .eq('id', shopId)
    .select()
    .single();

  if (error) throw error;
  return data;
};// ---- APPEND THIS TO src/store/services/shopService.js ----

/**
 * UPDATE: Toggle a shop's availability status between 'Active' and 'Offline'.
 * Deliberately restricted to those two values — 'Not Active' (brand new,
 * pending approval) is not something a shop owner can set themselves from
 * this control; it's set elsewhere in the approval flow.
 *
 * @param {string} shopId
 * @param {'Active' | 'Offline'} newStatus
 * @returns {Promise<object>} The updated shop record.
 */
export const updateShopAvailabilityStatus = async (shopId, newStatus) => {
    if (!['Active', 'Offline'].includes(newStatus)) {
        throw new Error(`Invalid availability status: ${newStatus}`);
    }

    const { data, error } = await supabase
        .from('shops')
        .update({
            shop_availability_status: newStatus,
            updated_at: new Date().toISOString(),
        })
        .eq('id', shopId)
        .select()
        .single();

    if (error) throw error;
    return data;
};

/**
 * UPDATE: Shop-level auto-accept + scheduling config.
 * @param {string} shopId
 * @param {{auto_accept_enabled?: boolean, auto_accept_reputation_levels?: string[], appointment_buffer_minutes?: number}} updates
 * Reputation levels are an explicit array from: 'new_user', 'bad', 'good',
 * 'great', 'outstanding' — the shop picks any combination, not a fixed tier.
 */
export const updateShopAutoAcceptConfig = async (shopId, updates) => {
    const payload = { ...updates, updated_at: new Date().toISOString() };

    const { data, error } = await supabase
        .from('shops')
        .update(payload)
        .eq('id', shopId)
        .select()
        .single();

    if (error) throw error;
    return data;
};

export async function getMonthlyReservationStats(shopId) {
    if (!shopId) return { totalThisMonth: 0, completedThisMonth: 0 };

    const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();

    const { data, error, count } = await supabase
        .from('reservations')
        .select('status', { count: 'exact' })
        .eq('shop_id', shopId)
        .gte('created_at', startOfMonth);

    if (error) throw new Error(error.message);

    const completedThisMonth = (data || []).filter((r) => r.status === 'picked_up').length;
    return { totalThisMonth: count || 0, completedThisMonth };
}

// src/store/services/shopService.js — add this function
export const getBusinessPaymentInfo = async (shopId) => {
    if (!shopId) return null;
    const { data, error } = await supabase
        .from('business_payment_info')
        .select('*')
        .eq('shop_id', shopId)
        .maybeSingle();

    if (error) throw error;
    return data;
};

/**
 * UPDATE: Update a shop's delivery/home service policy settings.
 *
 * @param {string} shopId
 * @param {object} servicePolicy - Updated service_policy object
 * @returns {Promise<object>} The updated shop record.
 */
export const updateShopDeliveryPolicy = async (shopId, servicePolicy) => {
    const { data, error } = await supabase
        .from('shops')
        .update({
            service_policy: servicePolicy,
            updated_at: new Date().toISOString(),
        })
        .eq('id', shopId)
        .select()
        .single();

    if (error) throw error;
    return data;
};